import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { BACKEND_URL, STAGE_LABELS } from "@/lib/api";
import { Building2, Mail, Phone, MapPin, ArrowLeft, FileText, FolderKanban, IndianRupee, DollarSign, Download, Printer } from "lucide-react";
import ClientLogo from "@/components/ClientLogo";
import { toast } from "sonner";

export default function ClientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [invs, setInvs] = useState([]);

  const load = async () => {
    const [s, j, i] = await Promise.all([
      api.get(`/clients/${id}/summary`),
      api.get(`/clients/${id}/jobs`),
      api.get(`/clients/${id}/invoices`),
    ]);
    setData(s.data);
    setJobs(j.data);
    setInvs(i.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) {
    return <div className="label-mono">Loading…</div>;
  }
  const c = data.client;
  const s = data.stats;

  return (
    <div className="space-y-6 animate-fade-up">
      <Link to="/clients" className="inline-flex items-center gap-1 label-mono hover:underline" data-testid="back-to-clients">
        <ArrowLeft className="w-3 h-3" /> ALL CLIENTS
      </Link>

      {/* Client header */}
      <div className="brut-card p-5 flex items-start gap-5 flex-wrap">
        <div className="w-24 h-24 border border-zinc-900 bg-zinc-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
          <ClientLogo client={c} alt={c.name} iconClass="w-10 h-10 text-zinc-400" />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="label-mono">CLIENT</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">{c.name}</h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-xs font-mono">
            {c.contact_person && <div><span className="label-mono mr-1">CONTACT</span>{c.contact_person}</div>}
            {c.team && <div><span className="label-mono mr-1">TEAM</span><span className="px-1.5 py-0.5 bg-zinc-100 border border-zinc-900">{c.team}</span></div>}
            {c.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</div>}
            {c.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</div>}
            {c.address && <div className="flex items-start gap-1 col-span-full"><MapPin className="w-3 h-3 mt-0.5" /><span className="truncate">{c.address}</span></div>}
            {c.gst_number && <div><span className="label-mono mr-1">GSTIN</span>{c.gst_number}</div>}
            <div><span className="label-mono mr-1">PER JOB</span>{c.currency} {c.default_rate_per_job}</div>
            <div><span className="label-mono mr-1">PER HOUR</span>{c.currency} {c.default_rate_per_hour}</div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="TOTAL JOBS" value={s.total_jobs} icon={FolderKanban} />
        <Stat label="ACTIVE JOBS" value={s.active_jobs} icon={FolderKanban} accent="bg-yellow-200" />
        <Stat label="INVOICES" value={s.total_invoices} icon={FileText} accent="bg-blue-200" />
        <Stat label="PAID" value={s.paid} icon={FileText} accent="bg-green-200" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="brut-card p-4 flex items-center justify-between">
          <div>
            <div className="label-mono">TOTAL BILLED ({c.currency})</div>
            <div className="font-heading text-3xl font-black mt-1">{c.currency === "USD" ? "$" : c.currency} {s.total_billed?.toLocaleString()}</div>
            <div className="text-xs font-mono text-zinc-500 mt-1">{s.draft} draft · {s.sent} sent · {s.paid} paid</div>
          </div>
          <DollarSign className="w-8 h-8 text-zinc-300" />
        </div>
        <div className="brut-card p-4 flex items-center justify-between">
          <div>
            <div className="label-mono">RECEIVED (INR)</div>
            <div className="font-heading text-3xl font-black mt-1">₹ {s.total_received_inr?.toLocaleString("en-IN")}</div>
            <div className="text-xs font-mono text-zinc-500 mt-1">Converted on invoice "paid" status</div>
          </div>
          <IndianRupee className="w-8 h-8 text-zinc-300" />
        </div>
      </div>

      {/* Monthly Statement */}
      <StatementCard clientId={id} clientName={c.name} currency={c.currency} />

      {/* Jobs */}
      <div className="brut-card p-4">
        <div className="label-mono mb-3">JOBS · {jobs.length}</div>
        {jobs.length === 0 ? (
          <div className="text-center py-8 text-zinc-400 font-mono text-xs">NO JOBS YET</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-zinc-900 text-white">
                  <Th>Job #</Th><Th>Project</Th><Th>Stage</Th><Th>Status</Th><Th>Priority</Th><Th align="right">Given</Th><Th align="right">Taken</Th><Th align="right">Eff %</Th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                    <Td><span className="font-mono text-xs">{j.job_number}</span></Td>
                    <Td className="font-bold">{j.project_name}</Td>
                    <Td><span className="font-mono text-[10px] uppercase font-bold px-1.5 py-0.5 bg-zinc-100 border border-zinc-900">{STAGE_LABELS[j.stage] || j.stage}</span></Td>
                    <Td><span className="font-mono text-xs">{j.status}</span></Td>
                    <Td><span className="font-mono text-[10px] uppercase">{j.priority || "normal"}</span></Td>
                    <Td align="right"><span className="font-mono">{j.given_time_hours}h</span></Td>
                    <Td align="right"><span className="font-mono">{j.taken_time_hours}h</span></Td>
                    <Td align="right"><span className="font-mono font-bold">{j.efficiency}%</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invoices */}
      <div className="brut-card p-4">
        <div className="label-mono mb-3">INVOICES · {invs.length}</div>
        {invs.length === 0 ? (
          <div className="text-center py-8 text-zinc-400 font-mono text-xs">NO INVOICES YET</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-zinc-900 text-white">
                  <Th>Invoice #</Th><Th>Period</Th><Th>Status</Th><Th align="right">Amount</Th><Th align="right">Paid INR</Th><Th>Rate</Th>
                </tr>
              </thead>
              <tbody>
                {invs.map((i) => {
                  const sym = { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[i.currency] || "";
                  return (
                    <tr key={i.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                      <Td><span className="font-mono text-xs">{i.invoice_number}</span></Td>
                      <Td><span className="font-mono text-xs">{i.period_start} → {i.period_end}</span></Td>
                      <Td><span className={`font-mono text-[10px] uppercase font-bold px-1.5 py-0.5 border border-zinc-900 ${i.status === "paid" ? "bg-green-200" : i.status === "sent" ? "bg-blue-200" : "bg-zinc-100"}`}>{i.status}</span></Td>
                      <Td align="right"><span className="font-mono font-bold">{sym}{i.total_amount?.toLocaleString()}</span></Td>
                      <Td align="right"><span className="font-mono">{i.total_amount_inr ? `₹${i.total_amount_inr.toLocaleString("en-IN")}` : "—"}</span></Td>
                      <Td><span className="font-mono text-xs text-zinc-500">{i.exchange_rate ? `@ ${i.exchange_rate}` : "—"}</span></Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatementCard({ clientId, clientName, currency }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [allTime, setAllTime] = useState(false);
  const [stmt, setStmt] = useState(null);
  const [senders, setSenders] = useState([]);
  const [senderId, setSenderId] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const SYM = { USD: "$", INR: "₹", EUR: "€", GBP: "£" };
  const sym = SYM[currency] || "";

  useEffect(() => {
    const params = allTime ? {} : { month };
    api.get(`/clients/${clientId}/statement`, { params }).then((r) => setStmt(r.data)).catch(() => setStmt(null));
  }, [clientId, month, allTime]);

  useEffect(() => {
    api.get("/invoice-senders").then((r) => {
      setSenders(r.data || []);
      if (r.data?.length) setSenderId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const downloadPdf = async () => {
    try {
      const params = allTime ? {} : { month };
      const res = await api.get(`/clients/${clientId}/statement/pdf`, { params, responseType: "blob" });
      const u = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = u; a.download = `statement_${clientName}_${allTime ? "all" : month}.pdf`; a.click();
      URL.revokeObjectURL(u);
    } catch { toast.error("Statement PDF failed"); }
  };

  const emailStatement = async () => {
    if (!senders.length) { toast.error("No email sender configured — add one under EMAIL SENDERS on the Invoices page"); return; }
    setEmailing(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/statement/send`, { month: allTime ? null : month, sender_id: senderId });
      toast.success(`Statement emailed to ${data.sent.to}`);
      if (showHistory) loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Email failed"); }
    setEmailing(false);
  };

  const loadHistory = () => {
    api.get(`/clients/${clientId}/statement/emails`).then((r) => setHistory(r.data || [])).catch(() => setHistory([]));
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && history === null) loadHistory();
  };

  const byCur = Object.entries(stmt?.totals_by_currency || {}).sort();
  const multi = byCur.length > 1;
  const t = stmt?.totals || { billed: 0, received: 0, outstanding: 0, invoice_count: 0 };
  const rowSym = (c) => SYM[c] || "";
  return (
    <div className="brut-card p-4" data-testid="statement-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="label-mono flex items-center gap-2"><Printer className="w-3.5 h-3.5" /> ACCOUNT STATEMENT</div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="month" disabled={allTime} className="brut-input px-2 py-1.5 text-xs font-mono disabled:opacity-50" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="statement-month-input" />
          <label className="flex items-center gap-1 text-xs font-mono cursor-pointer">
            <input type="checkbox" className="w-3.5 h-3.5 accent-zinc-900" checked={allTime} onChange={(e) => setAllTime(e.target.checked)} data-testid="statement-alltime-check" /> ALL TIME
          </label>
          <button onClick={downloadPdf} className="brut-btn px-3 py-1.5 text-xs flex items-center gap-1" data-testid="statement-pdf-btn">
            <Download className="w-3.5 h-3.5" /> STATEMENT PDF
          </button>
          {senders.length > 1 && (
            <select value={senderId} onChange={(e) => setSenderId(e.target.value)} className="brut-input px-2 py-1.5 text-xs font-mono max-w-[150px]" data-testid="statement-sender-select">
              {senders.map((s) => <option key={s.id} value={s.id}>{s.email}</option>)}
            </select>
          )}
          <button onClick={emailStatement} disabled={emailing} className="brut-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1 border-blue-700 text-blue-800 hover:bg-blue-50 disabled:opacity-60" data-testid="statement-email-btn">
            <Mail className="w-3.5 h-3.5" /> {emailing ? "SENDING…" : "EMAIL STATEMENT"}
          </button>
          <button onClick={toggleHistory} className={`brut-btn-secondary px-3 py-1.5 text-xs font-mono ${showHistory ? "bg-zinc-900 text-white" : ""}`} data-testid="statement-history-btn">
            SENT HISTORY
          </button>
        </div>
      </div>
      {multi ? (
        <div className="mb-3 space-y-1" data-testid="statement-multicurrency">
          <div className="label-mono">PER-CURRENCY TOTALS</div>
          {byCur.map(([cur, b]) => (
            <div key={cur} className="grid grid-cols-4 gap-2 text-center border border-zinc-900">
              <div className="p-1.5 bg-zinc-900 text-white font-mono text-xs font-bold">{cur} · {b.invoice_count} inv</div>
              <div className="p-1.5 font-mono text-xs">BILLED <b>{rowSym(cur)}{b.billed.toLocaleString()}</b></div>
              <div className="p-1.5 font-mono text-xs text-green-800 bg-green-50">RECV <b>{rowSym(cur)}{b.received.toLocaleString()}</b></div>
              <div className={`p-1.5 font-mono text-xs ${b.outstanding > 0 ? "text-red-800 bg-red-50" : ""}`}>DUE <b>{rowSym(cur)}{b.outstanding.toLocaleString()}</b></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-center">
          <div className="border border-zinc-900 p-2 bg-zinc-50"><div className="label-mono">INVOICES</div><div className="font-heading text-xl font-black">{t.invoice_count}</div></div>
          <div className="border border-zinc-900 p-2 bg-zinc-50"><div className="label-mono">BILLED</div><div className="font-heading text-xl font-black">{sym}{t.billed.toLocaleString()}</div></div>
          <div className="border border-zinc-900 p-2 bg-green-50"><div className="label-mono">RECEIVED</div><div className="font-heading text-xl font-black text-green-800">{sym}{t.received.toLocaleString()}</div></div>
          <div className={`border border-zinc-900 p-2 ${t.outstanding > 0 ? "bg-red-50" : "bg-zinc-50"}`} data-testid="statement-outstanding"><div className="label-mono">OUTSTANDING</div><div className={`font-heading text-xl font-black ${t.outstanding > 0 ? "text-red-800" : ""}`}>{sym}{t.outstanding.toLocaleString()}</div></div>
        </div>
      )}
      {showHistory && (
        <div className="mb-3 border border-zinc-900 bg-zinc-50 p-3" data-testid="statement-history-panel">
          <div className="label-mono mb-2">STATEMENT EMAILS SENT · {(history || []).length}</div>
          {history === null && <div className="font-mono text-xs text-zinc-500">Loading…</div>}
          {history && history.length === 0 && <div className="font-mono text-xs text-zinc-500">NO STATEMENTS EMAILED YET</div>}
          {history && history.length > 0 && (
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-zinc-900 text-white">
                  <Th>Sent</Th><Th>To</Th><Th>Period</Th><Th>Outstanding At Send</Th><Th>From</Th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-zinc-200" data-testid={`statement-log-${h.id}`}>
                    <Td><span className="font-mono">{(h.sent_at || "").slice(0, 16).replace("T", " ")}</span></Td>
                    <Td><span className="font-mono">{h.to}</span></Td>
                    <Td><span className="font-mono">{h.month || "All time"}</span></Td>
                    <Td><span className="font-mono font-bold text-red-700">{h.outstanding}</span></Td>
                    <Td><span className="font-mono text-zinc-500">{h.sender_email}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {stmt && stmt.invoices.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-zinc-900 text-white">
                <Th>Invoice #</Th><Th>Period</Th><Th>Jobs</Th><Th>Status</Th><Th align="right">Total</Th><Th align="right">Received</Th><Th align="right">Balance</Th>
              </tr>
            </thead>
            <tbody>
              {stmt.invoices.map((r) => {
                  const rSym = { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[r.currency] || sym;
                  return (
                  <tr key={r.invoice_id} className="border-b border-zinc-200">
                  <Td><span className="font-mono">{r.invoice_number}</span></Td>
                  <Td><span className="font-mono">{r.period_start} → {r.period_end}</span></Td>
                  <Td><span className="font-mono">{r.job_count}</span></Td>
                  <Td><span className={`font-mono text-[10px] uppercase font-bold px-1.5 py-0.5 border border-zinc-900 ${r.status === "paid" ? "bg-green-200" : r.status === "sent" ? "bg-blue-200" : "bg-zinc-100"}`}>{r.status}</span></Td>
                  <Td align="right"><span className="font-mono">{rSym}{r.total_amount.toFixed(2)}</span></Td>
                  <Td align="right"><span className="font-mono text-green-800">{rSym}{r.amount_received.toFixed(2)}</span></Td>
                  <Td align="right"><span className={`font-mono font-bold ${r.balance_due > 0.005 ? "text-red-700" : "text-green-700"}`}>{rSym}{Math.max(r.balance_due, 0).toFixed(2)}</span></Td>
                </tr>
                  );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-6 text-zinc-400 font-mono text-xs">NO INVOICES IN THIS PERIOD</div>
      )}
    </div>
  );
}

const Stat = ({ label, value, icon: Icon, accent = "bg-zinc-100" }) => (
  <div className="brut-card p-3 flex items-center justify-between">
    <div>
      <div className="label-mono">{label}</div>
      <div className="font-heading text-2xl font-black mt-1">{value}</div>
    </div>
    <div className={`w-9 h-9 border border-zinc-900 flex items-center justify-center ${accent}`}>
      <Icon className="w-4 h-4" />
    </div>
  </div>
);
const Th = ({ children, align = "left" }) => <th className={`text-${align} px-3 py-2 label-mono text-white border-b border-zinc-900 whitespace-nowrap`}>{children}</th>;
const Td = ({ children, align = "left", className = "" }) => <td className={`text-${align} px-3 py-2.5 ${className}`}>{children}</td>;
